import React, { useState, useRef, useEffect } from 'react';
import { Box, InputBase, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

function SearchComponent({ value = '', onSearchChange }) {
  const [searchTerm, setSearchTerm] = useState(value);
  // Keep the box expanded on mount if it already has a retained search value
  const [isExpanded, setIsExpanded] = useState(Boolean(value));
  const inputRef = useRef(null);

  // Sync internal state with the controlled `value` prop. This makes the
  // component honor the parent's persistent search state, so when it remounts
  // (e.g. switching tabs and returning) the input reflects the retained query
  // instead of showing empty while the table still shows filtered results.
  useEffect(() => {
    setSearchTerm(value);
    if (value) {
      setIsExpanded(true);
    }
  }, [value]);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 50);
    }
  }, [isExpanded]);

  const handleOpen = () => {
    setIsExpanded(true);
  };

  const handleClose = () => {
    // Only collapse if the search box is completely empty
    if (!searchTerm.trim()) {
      setIsExpanded(false);
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    onSearchChange(value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      inputRef.current?.blur();
    } else if (event.key === 'Escape') {
      handleClear(event);
    }
  };

  const handleClear = (event) => {
    if (event) event.stopPropagation();
    setSearchTerm('');
    onSearchChange('');
    setIsExpanded(false); 
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
      
      <Box
        onClick={!isExpanded ? handleOpen : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '38px',
          
          // THE FIX: Using explicit media queries prevents MUI from snapping the CSS classes
          width: isExpanded ? '400px' : '105px',
          '@media (max-width: 900px)': {
            width: isExpanded ? '320px' : '105px',
          },
          '@media (max-width: 600px)': {
            width: isExpanded ? '220px' : '105px',
          },
          
          borderRadius: '8px',
          border: '1px solid',
          borderColor: isExpanded ? 'divider' : '#1976d2',
          bgcolor: isExpanded ? 'background.paper' : 'transparent',
          boxShadow: isExpanded ? '0px 4px 20px rgba(0, 0, 0, 0.08)' : 'none',
          cursor: isExpanded ? 'text' : 'pointer',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
          overflow: 'hidden',
          px: 1,
          '&:hover': {
            bgcolor: !isExpanded ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
          },
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: '0px 4px 20px rgba(25, 118, 210, 0.15)',
          }
        }}
      >
        <SearchIcon 
          sx={{ 
            ml: 0.5, 
            mr: 1, 
            fontSize: 20,
            color: isExpanded ? 'text.secondary' : '#1976d2', 
            transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)' 
          }} 
        />
        
        <Box sx={{ flex: 1, position: 'relative', height: '100%' }}>
          
          <Typography 
            sx={{ 
              position: 'absolute', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: '#1976d2',
              opacity: isExpanded ? 0 : 1,
              transition: 'opacity 0.3s ease',
              pointerEvents: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap' // Prevents the text from stacking while shrinking
            }}
          >
            Search
          </Typography>

          <InputBase
            inputRef={inputRef}
            placeholder="Search ..."
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onBlur={handleClose}
            sx={{ 
              height: '100%', 
              width: '100%',
              opacity: isExpanded ? 1 : 0,
              transition: 'opacity 0.3s ease',
              pointerEvents: isExpanded ? 'auto' : 'none',
              fontSize: '0.95rem'
            }}
          />
        </Box>

        <IconButton 
          size="small" 
          onClick={handleClear} 
          sx={{ 
            opacity: (isExpanded && searchTerm) ? 1 : 0,
            pointerEvents: (isExpanded && searchTerm) ? 'auto' : 'none',
            transform: (isExpanded && searchTerm) ? 'scale(1)' : 'scale(0.8)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            color: 'text.secondary', 
            p: 0.5,
            mr: -0.5
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

    </Box>
  );
}

SearchComponent.propTypes = {
  value: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
};

export default SearchComponent;